import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { CheckCircle2, Info, XCircle, X } from 'lucide-react';
import { cn } from '@mt/ui';

type ToastTone = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

const ToastContext = createContext<{ show(message: string, tone?: ToastTone): void } | null>(null);

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts((all) => all.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (message: string, tone: ToastTone = 'success') => {
      const id = ++counter;
      setToasts((all) => [...all.slice(-2), { id, tone, message }]);
      setTimeout(() => dismiss(id), tone === 'error' ? 7000 : 4000);
    },
    [dismiss],
  );
  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-relevant="additions"
        className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-50 flex flex-col items-center gap-2 px-4 md:bottom-6"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === 'error' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex w-full max-w-md animate-rise items-center gap-3 rounded-xl border px-4 py-3 shadow-raised',
              toast.tone === 'success' && 'border-fern/30 bg-surface text-ink',
              toast.tone === 'error' && 'border-rose/30 bg-surface text-ink',
              toast.tone === 'info' && 'border-line bg-surface text-ink',
            )}
          >
            {toast.tone === 'success' && <CheckCircle2 className="size-5 shrink-0 text-fern" aria-hidden="true" />}
            {toast.tone === 'error' && <XCircle className="size-5 shrink-0 text-rose" aria-hidden="true" />}
            {toast.tone === 'info' && <Info className="size-5 shrink-0 text-primary" aria-hidden="true" />}
            <p className="flex-1 text-sm font-medium">{toast.message}</p>
            <button type="button" onClick={() => dismiss(toast.id)} className="grid size-8 place-items-center rounded-full text-muted hover:bg-surface-2" aria-label="Dismiss">
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
